/** Canonical marketing origin — always www (matches sitemap + GSC property). */
export const SITE_ORIGIN = 'https://www.gograbjob.com';

const DEFAULT_B2C_FRONTEND_URL_TEST = 'http://localhost:5173';
const DEFAULT_B2C_FRONTEND_URL_PROD = SITE_ORIGIN;

export function siteUrl(path = ''): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_ORIGIN}${p === '/' ? '' : p}`;
}

function stripTrailingSlash(url: string): string {
  return url.replace(/\/$/, '');
}

/** localhost / 127.0.0.1 / *.local — used for payment-return allowlist bypass. */
function isLocalDevB2cHost(hostname?: string): boolean {
  if (typeof window === 'undefined' && !hostname) return false;
  const h = String(hostname ?? window.location.hostname).toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local');
}


/** True when the B2C build targets Cashfree production (not sandbox). */
export function isB2cProductionEnvironment(): boolean {
  const cashfree = import.meta.env.VITE_B2C_CASHFREE_ENV?.toUpperCase()
    ?? import.meta.env.VITE_CASHFREE_ENV?.toUpperCase();
  if (cashfree === 'PROD' || cashfree === 'PRODUCTION') return true;
  if (cashfree === 'TEST' || cashfree === 'SANDBOX') return false;
  return import.meta.env.VITE_APP_ENV === 'production';
}

/**
 * B2C frontend base URL for the active environment.
 * Pair with Supabase secrets B2C_FRONTEND_URL_TEST / B2C_FRONTEND_URL_PROD.
 */
export function resolveB2cFrontendUrl(): string {
  const testUrl = stripTrailingSlash(
    import.meta.env.VITE_B2C_FRONTEND_URL_TEST
    || import.meta.env.VITE_FRONTEND_URL
    || DEFAULT_B2C_FRONTEND_URL_TEST,
  );
  const prodUrl = stripTrailingSlash(
    import.meta.env.VITE_B2C_FRONTEND_URL_PROD
    || import.meta.env.VITE_FRONTEND_URL
    || DEFAULT_B2C_FRONTEND_URL_PROD,
  );

  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    if (host === 'gograbjob.com' || host === 'www.gograbjob.com') {
      return prodUrl;
    }
  }

  return isB2cProductionEnvironment() ? prodUrl : testUrl;
}

/** Payment/OAuth return URLs — current tab on local dev; env-resolved on deployed hosts. */
export function getCanonicalSiteOrigin(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    if (host === 'gograbjob.com' || host === 'www.gograbjob.com') {
      return SITE_ORIGIN;
    }
    // Always return to the tab the user started checkout from (any port / 127.0.0.1).
    if (isLocalDevB2cHost(host)) {
      return window.location.origin;
    }
  }
  if (typeof window === 'undefined') {
    return isB2cProductionEnvironment()
      ? DEFAULT_B2C_FRONTEND_URL_PROD
      : DEFAULT_B2C_FRONTEND_URL_TEST;
  }
  return resolveB2cFrontendUrl();
}

/** In-app navigation (dashboard, retry checkout) — always stay on current host. */
export function getAppOrigin(): string {
  if (typeof window !== 'undefined') return window.location.origin;
  return resolveB2cFrontendUrl();
}
